/* @flow */
// some hooks are used client side to focus on first error.
// we need access to DOM node for this.
/* global document */
import * as React from 'react';

import get from 'lodash/get';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createSelector } from 'reselect';

import { withPageMeta } from '@gandi/app-runner';
import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import {
  selectOneProduct,
  selectCartUUID,
  selectCartIsActive,
  getCart,
  createCart,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import * as coupons from '@gandi/redux-resource-coupon';
import { instances as instancesPrice } from '@gandi/redux-resource-price';
import type { InstanceSize } from '@gandi/redux-resource-simplehosting';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { Resource } from '@gandi/resource/redux';

import { withTranslator } from '@gandi/translate';

import {
  withForm,
  getFormValues,
  createValidator,
  SubmissionError,
} from 'gandi.react-form';
import { withLogger, createLogger } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';

import { selectSearchValue } from '../../DomainCreate/modules/selectors/search';
import type { Dispatch, FullState, GetState } from '../../types';
import { fetchInstancePrices, selectSharedFeatures } from '../modules/offers';
import * as types from '../modules/types';
import {
  upsertProduct,
  computeInitialValues,
  createUpsertErrorToSubmissionError,
  type APIFieldName,
  type FormFieldName,
  type UpsertProductResponseSuccess,
  type UpsertProductError,
} from '../modules/upsertProduct';
import * as urls from '../modules/urls';

import SimpleHostingCreatePresenter, {
  type PresenterProps,
  type PresenterDispatchProps,
  type PresenterOwnProps,
  type PresenterStateProps,
} from './components/Presenter';

// ---------------------------------------------------------------- Define types

export type InstanceCreateFormValues = {
  size: InstanceSize | 'trial',
  duration?: string,
  duration_unit?: 'd' | 'm',
  ...
};

type Options = {
  discounts?: Array<string>,
  ...
};
// ---------------------------------------------------------------- Some tooling

const formName = 'instance-create-form';

const logger = createLogger('SimpleHosting/Create');

/**
 * defines mapping between the form fields (left) and the product fields (right).
 */
const fieldMap: { [key: FormFieldName]: APIFieldName, ... } = {
  size: ['size', 'trial'],
  duration: 'duration',
  duration_unit: 'duration_unit',
};

// ------------------------------------------------------------- Form validation

const validateSize = (
  value: ?string,
  data: InstanceCreateFormValues,
  props: PresenterProps,
): ?string => {
  const prices: any[] = props.instancePrices.trial
    ? [{ options: { size: 'trial' } }].concat(props.instancePrices.all)
    : props.instancePrices.all;

  if (value && prices.some(item => item.options.size === value)) {
    return null;
  }

  return props.translator.__('Please select a valid size.');
};

const validate: any = createValidator({
  size: validateSize,
});

// ------------------------------------------------------------- Form submission

const upsertErrorToSubmissionError: (
  error: SubmissionError | UpsertProductError | Error,
  dispatch: Dispatch,
  props: PresenterProps,
) => UpsertProductResponseSuccess = createUpsertErrorToSubmissionError(
  fieldMap,
  logger,
);

const onSubmit = (
  values: InstanceCreateFormValues,
  dispatch: Dispatch,
  props: PresenterProps,
): Promise<UpsertProductResponseSuccess | SubmissionError> =>
  dispatch(async (_: any, getState: () => FullState) => {
    let pricesForSize;
    if (values.size === 'trial' && props.instancePrices.trial) {
      pricesForSize = props.instancePrices.trial;
    } else {
      pricesForSize = instancesPrice.selectPriceForSize(getState(), {
        size: values.size,
        process: 'create',
      }) || {
        options: {
          size: values.size,
          trial: false,
        },
        minDuration: 1,
        maxDuration: 12,
        durationUnit: 'm',
      };
    }

    const duration = values.duration
      ? Number.parseInt(values.duration, 10)
      : pricesForSize.maxDuration;
    const duration_unit = values.duration_unit || pricesForSize.durationUnit;

    const body = {
      product_type: 'paas',
      process: 'create',
      payload: {
        label: props.translator.__('Web Hosting'),
        size: pricesForSize.options.size,
        trial: false,
      },
    };

    if (props.searchParams.has('cross_sell')) {
      // $FlowFixMe
      body.metadatas = {
        cross_sell: {
          view: 'overview',
          origin: 'domain_create',
        },
      };

      // Retrieve the last domain suggest search inputs for cross-sell metadata.
      const domainSuggestSearchInputValue = selectSearchValue(getState());
      if (domainSuggestSearchInputValue) {
        // $FlowFixMe
        body.metadatas.cross_sell.query = domainSuggestSearchInputValue;
      }

      // Add cross-sell reason in payload so we can apply specifics if needed.
      if (props.searchParams.has('reason')) {
        // $FlowFixMe
        body.payload.cross_sell_reason = props.searchParams.get('reason');
      }
    }

    if (pricesForSize.options.trial) {
      body.payload = {
        ...body.payload,
        trial: true,
      };
    } else {
      body.payload = {
        ...body.payload,
        trial: false,
        duration,
        duration_unit,
      };
    }

    const addProduct = () =>
      dispatch(
        upsertProduct({
          // $FlowFixMe[incompatible-call]
          productData: body,
          productId: props.params.productId,
        }),
      ).catch(err => {
        // Replace url when product wad created
        const productId = get(err, 'response.data.id');
        if (!props.params.productId && productId) {
          // replace current url with fully qualified URL
          props.navigate(urls.selectCreateUrl(getState(), { productId }), {
            replace: true,
          });
        }

        return upsertErrorToSubmissionError(err, dispatch, props);
      });

    // Why we should do this check while is the job of the cart middleware?
    if (!props.cartExists) {
      const { currency, country } = props;
      return dispatch(createCart({ currency, country }))
        .then(() => dispatch(getCart()))
        .then(() => addProduct())
        .catch(err => Promise.reject(err))
        .finally(() => dispatch(getCart()));
    }

    return addProduct()
      .catch(err => Promise.reject(err))
      .finally(() => dispatch(getCart));
  }).catch(err => {
    if (err instanceof SubmissionError) {
      throw err;
    }

    throw new SubmissionError(err);
  });

const onSubmitSuccess = (
  response: UpsertProductResponseSuccess,
  dispatch: Dispatch,
  props: PresenterProps,
): void => {
  // Dispatching an action allows us to access current state.
  // Props received here computed BEFORE form submission, this means cart might
  // be absent.
  // Anything that requires cartId access is not sure to be resolve with
  // previous version of the state, so current state is required.
  dispatch((_: any, getState: () => FullState): void => {
    // NOTE: product id seems to be omited on update,
    // so we need to get it from URL parameters.
    let productId;
    if ('id' in response.data && response.data.id) {
      productId = response.data.id;
    } else if ('productId' in props.params && props.params.productId) {
      productId = props.params.productId;
    } else {
      dispatch(
        logger.error(
          { res: response, body: { params: props.params } },
          'onSubmitSuccess: Unable to retrieve product id',
        ),
      );
      productId = '';
    }

    // replace current url with fully qualified URL
    props.navigate(
      urls.selectCreateUrl(getState(), { productId: String(productId) }),
      { replace: true },
    );

    // redirect to overview
    props.navigate(urls.selectOverviewUrl(getState()));
  });
};

const onSubmitFail = (
  errors: { [FormFieldName]: string, ... },
  dispatch: Dispatch,
): void => {
  // focus on first error.
  dispatch(logger.info({ errors }, 'validation errors'));

  // exit if server
  if (!document) return undefined;

  const errorFieldName = Object.keys(errors)[0];
  if (!errorFieldName) return undefined;

  const elt = document.querySelector(
    `form#${formName} [name=${errorFieldName}]`,
  );
  if (!elt) return undefined;

  elt.focus();
  return undefined;
};

// ---------------------------------------------- Define some specific selectors

// Check wether cart is valid.
// A cart is valid if it is not yet created OR if it is active.
const selectCartIsValid: (state: FullState) => boolean = createSelector(
  [selectCartUUID, selectCartIsActive],
  (cartExists, cartIsActive) => !cartExists || cartIsActive,
);

// ------------------------------------------------------------ Specific actions

const shouldFetchResource = (state: FullState, resource: Resource<>): boolean =>
  !(resource.selectIsFailure(state) && resource.selectIsFetching(state));

/**
 * Action to load all required data.
 * It is used in both preFetch and componentDidMount.
 */
const fetchRequiredData =
  (discountCode?: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const sharingId = selectCurrentId(state);

    const options: Options = {};

    if (discountCode) {
      options.discounts = [discountCode];
    }

    return Promise.allSettled([
      // $FlowFixMe[prop-missing]
      // $FlowFixMe[incompatible-variance]
      shouldFetchResource(state, instancesPrice) &&
        dispatch(fetchInstancePrices(options)),
      sharingId &&
        // $FlowFixMe[prop-missing]
        // $FlowFixMe[incompatible-call]
        // $FlowFixMe[incompatible-exact]
        // $FlowFixMe[incompatible-variance]
        shouldFetchResource(state, coupons) &&
        dispatch(coupons.fetchAll({ sharingId })),
    ]).catch(err => {
      // Clean-up server-side state in case of error.
      // This is required to prevent transmission of loading status
      // for other resources.
      if (global.__SERVER__) {
        if (instancesPrice.selectIsFetching(getState())) {
          dispatch(instancesPrice.abort());
        }
      }

      // And obviously log error
      dispatch(
        logger.error(
          { err },
          'Unable to render SimpleHosting/Create due to missing required data.',
        ),
      );
    });
  };

// ----------------------------------------------------------- Connect component
const getAllFormValues = getFormValues(formName);

const mapStateToProps = (
  state: FullState,
  ownProps: PresenterOwnProps,
): PresenterStateProps => {
  const allFormValues = getAllFormValues(state) || {};

  const product = ownProps.params.productId
    ? selectOneProduct(state, { productId: ownProps.params.productId }) || {}
    : {};

  const initialValues = computeInitialValues(
    fieldMap,
    // $FlowFixMe[incompatible-call]
    product,
    ownProps.location.query,
  );

  if (product && product.payload && product.payload.trial) {
    initialValues.size = 'trial';
  }

  const { currency, country } = selectPricingInfos(state);

  return {
    backUrl: urls.selectAdminShsUrl(state),
    cartExists: !!selectCartUUID(state) && selectCartIsActive(state),
    cartIsValid: selectCartIsValid(state),
    currency,
    country,
    // Required data status
    isFetching: instancesPrice.selectIsFetching(state),
    isLoaded: instancesPrice.selectIsLoaded(state),
    isFailure: instancesPrice.selectIsFailure(state),
    formName,
    formValues: allFormValues,
    initialValues,
    // FIXME(tim): flatten or memoize
    instancePrices: {
      // All possible instance prices
      all: instancesPrice.selectPricesArray(state),
      // available instance prices
      available: instancesPrice.selectPricesArray(state),
      // features shared by every prices
      sharedFeatures: selectSharedFeatures(state),
      // the trial offer if available
      trial: instancesPrice.selectTrialForSize(state, { size: 'm' }),
    },
    recommendedFeatures: types.selectRecommendedFeatures(state),
    overviewUrl: urls.selectOverviewUrl(state),
    taxesIncluded: selectTaxesDisplaySetting(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch): PresenterDispatchProps => ({
  fetchRequiredData: (...args) => dispatch(fetchRequiredData(...args)),
  notifyDanger: (...args) => dispatch(notifyDanger(...args)),
});

export default (compose(
  withPageMeta(args => ({
    title: args.__('Create a new instance - Simple Hosting - Gandi.net'),
  })),
  withRouter,
  withLogger('Simple Hosting Create'),
  withTranslator({ propsNamespace: 'translator' }),
  connect(mapStateToProps, mapDispatchToProps),
  withForm({
    propNamespace: 'form',
    form: formName,
    validate,
    onSubmit,
    onSubmitSuccess,
    onSubmitFail,
  }),
)(SimpleHostingCreatePresenter): React.ComponentType<{}>);
